import { ref, watch, onUnmounted, type Ref } from "vue"
import { clamp, map } from "@/utils/math"

const INTERVAL = 1000
const MOUSE_THRESHOLD = 50

const CHARGE_FULL_MS = 1200
const MIN_CHARGE = 0.15
const MIN_HOLD_MS = 0 // below this, a press is a "click", not a hold → reject
const HOVER_GROW = 0.667 // hovered letter's share of the column (~2:1)
const CHARGE_GROW = 0.92 // held letter's share at full charge (near-takeover)
const CHARGE_TREMBLE_MAX = 0.16 // peak per-frame jitter velocity at full charge
const SPIN_MS_MIN = 1800
const SPIN_MS_MAX = 5200
const START_INTERVAL_MAX = 70
const START_INTERVAL_MIN = 32
const END_INTERVAL = 750
const DECEL_POWER = 4

const SPRING_EPSILON = 0.0005

export type JackpotState =
  | "intro"
  | "idle"
  | "charging"
  | "spinning"
  | "result"
  | "paused"

let sharedTicker: ReturnType<typeof setInterval> | null = null
let instance: JackpotInstance | null = null

interface JackpotInstance {
  ensurePending: () => void
  isPendingReady: () => boolean
  commit: () => void
}

function ensureSharedTicker() {
  if (!sharedTicker) {
    sharedTicker = setInterval(() => {
      if (!instance) return
      instance.ensurePending()
      if (instance.isPendingReady()) {
        instance.commit()
        instance.ensurePending()
      }
    }, INTERVAL)
  }
}

function releaseSharedTicker() {
  if (!instance && sharedTicker) {
    clearInterval(sharedTicker)
    sharedTicker = null
  }
}

function fisherYates<T>(arr: T[]): T[] {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function easeIn(t: number): number {
  return Math.pow(t, DECEL_POWER)
}

/**
 * Jackpot — a press-charge-spin "slot machine" over two letterform images.
 * Layout is driven by a real spring integrator (`split` 0→1 = C's share).
 */
export function useJackpot(
  urls: Ref<string[]>,
  options?: { minPool?: number; mouseBothLetters?: boolean },
) {
  const minPool = Math.max(2, options?.minPool ?? 7)
  const mouseBothLetters = options?.mouseBothLetters ?? false

  const state = ref<JackpotState>("intro")
  const poolReady = ref(false)
  const cImage = ref("")
  const mImage = ref("")
  const readyCount = ref(0)

  const chargeLevel = ref(0)
  const pressedLetter = ref<"c" | "m" | null>(null)
  const hoveredLetter = ref<"c" | "m" | null>(null)

  // Spring-driven split: C's share of vertical space (M gets 1 - split).
  const split = ref(0.5)

  const readyPool: string[] = []
  const seen = new Set<string>()
  const imageCache = new Map<string, HTMLImageElement>()
  let deck: string[] = []
  let lastC = ""
  let lastM = ""

  let lastMouseC = { x: 0, y: 0 }
  let lastMouseM = { x: 0, y: 0 }

  let pendingC: string | null = null
  let pendingM: string | null = null
  let pendingCDecoded = false
  let pendingMDecoded = false

  // Winner chosen at the moment of release, so both rows can land on it together.
  let finalImage: string | null = null

  // ── Spring integrator ──────────────────────────────────────────────────────

  let springValue = 0.5
  let springVel = 0
  let springTarget = 0.5
  let springK = 170
  let springC = 26
  let springRaf: number | null = null
  let lastSpringTime = 0

  function setSpringTarget(target: number, params: { k: number; c: number }) {
    springTarget = target
    springK = params.k
    springC = params.c
    wakeSpring()
  }

  function kickSpring(velocity: number) {
    springVel += velocity
    wakeSpring()
  }

  function stepSpring(dt: number) {
    const sub = dt / 4
    for (let i = 0; i < 4; i++) {
      const force =
        -springK * (springValue - springTarget) - springC * springVel
      springVel += force * sub
      springValue += springVel * sub
    }
    split.value = clamp(springValue, 0, 1)
  }

  function springLoop(now: number) {
    const dt = Math.min((now - lastSpringTime) / 1000, 0.05)
    lastSpringTime = now
    stepSpring(dt)

    const awake =
      Math.abs(springVel) > SPRING_EPSILON ||
      Math.abs(springValue - springTarget) > SPRING_EPSILON

    if (awake) {
      springRaf = requestAnimationFrame(springLoop)
    } else {
      springValue = springTarget
      springVel = 0
      split.value = clamp(springValue, 0, 1)
      springRaf = null
    }
  }

  function wakeSpring() {
    if (springRaf !== null) return
    lastSpringTime = performance.now()
    springRaf = requestAnimationFrame(springLoop)
  }

  function stopSpringLoop() {
    if (springRaf !== null) {
      cancelAnimationFrame(springRaf)
      springRaf = null
    }
  }

  // ── Image pool ─────────────────────────────────────────────────────────────

  function addReady(url: string) {
    readyPool.push(url)
    readyCount.value++
    const isReady =
      readyPool.length >= minPool || readyPool.length >= urls.value.length
    if (isReady) poolReady.value = true
  }

  // Loads + decodes an image and RETAINS the element, so (a) the request can't
  // be GC-cancelled, and (b) the live, decoded resource is reused without a
  // fresh fetch when the same URL is later set as an <img src> — even with the
  // HTTP cache disabled. Resolves true only when the image is actually
  // paintable, and never rejects, so a single failure can't stall the gates.
  function loadImage(url: string): Promise<boolean> {
    const cached = imageCache.get(url)
    if (cached?.complete && cached.naturalWidth > 0)
      return Promise.resolve(true)

    const img = cached ?? new Image()
    imageCache.set(url, img)
    if (!img.src) img.src = url

    return new Promise<boolean>(resolve => {
      const finish = () => resolve(img.complete && img.naturalWidth > 0)
      if (img.decode)
        img.decode().then(finish, finish) // resolve on reject too
      else img.onload = img.onerror = finish
    })
  }

  function preload(url: string) {
    // Decode (not just load) before adding to the pool, so the first flash of
    // each image during a spin is instant. Broken images are skipped from the
    // pool, but the promise always settles, so it can't stall the ready gate.
    loadImage(url).then(ok => {
      if (ok) addReady(url)
    })
  }

  watch(
    urls,
    newUrls => {
      for (const url of newUrls) {
        if (!seen.has(url)) {
          seen.add(url)
          preload(url)
        }
      }
    },
    { immediate: true, deep: true },
  )

  function pickPair(): [string, string] | null {
    if (readyPool.length < 2) return null

    if (deck.length < 2) {
      deck = fisherYates(readyPool)
      if (deck.length > 1 && deck[deck.length - 1] === lastC) {
        const i = Math.floor(Math.random() * (deck.length - 1))
        ;[deck[deck.length - 1], deck[i]] = [deck[i], deck[deck.length - 1]]
      }
    }

    const nextC = deck.pop()!

    let nextM: string | undefined
    const skipped: string[] = []
    while (deck.length > 0) {
      const candidate = deck.pop()!
      if (candidate !== nextC) {
        nextM = candidate
        break
      }
      skipped.push(candidate)
    }
    deck.push(...skipped)

    if (nextM === undefined) {
      nextM = readyPool.find(u => u !== nextC) ?? nextC
    }

    return [nextC, nextM]
  }

  function pickSingleFor(letter: "c" | "m"): string | null {
    const other = letter === "c" ? mImage.value : cImage.value
    const pool = readyPool.filter(u => u !== other)
    if (!pool.length) return null
    return pool[Math.floor(Math.random() * pool.length)]
  }

  function pickWinner(): string {
    if (readyPool.length === 0) return cImage.value || mImage.value
    return readyPool[Math.floor(Math.random() * readyPool.length)]
  }

  function pickAndCommit() {
    const pair = pickPair()
    if (!pair) return
    const [c, m] = pair
    lastC = c
    lastM = m
    cImage.value = c
    mImage.value = m
  }

  function ensurePending() {
    if (pendingC !== null && pendingM !== null) return
    const pair = pickPair()
    if (!pair) return
    const [c, m] = pair
    pendingC = c
    pendingM = m
    pendingCDecoded = false
    pendingMDecoded = false
    // Pair URLs come from readyPool (already decoded + retained), so these
    // settle near-instantly. Flag on settle regardless of success, so a single
    // failure can never freeze the ambient commit ticker.
    loadImage(c).then(() => {
      if (pendingC === c) pendingCDecoded = true
    })
    loadImage(m).then(() => {
      if (pendingM === m) pendingMDecoded = true
    })
  }

  function isPendingReady() {
    return pendingCDecoded && pendingMDecoded
  }

  function commit() {
    if (!pendingC || !pendingM) return
    lastC = pendingC
    lastM = pendingM
    cImage.value = pendingC
    mImage.value = pendingM
    pendingC = null
    pendingM = null
    pendingCDecoded = false
    pendingMDecoded = false
  }

  const subscriber: JackpotInstance = { ensurePending, isPendingReady, commit }

  // ── Charge / spin loops ────────────────────────────────────────────────────

  let chargeRaf: number | null = null
  let chargeStart = 0
  let spinRaf: number | null = null

  function detachAmbient() {
    if (instance === subscriber) instance = null
    pendingC = null
    pendingM = null
    pendingCDecoded = false
    pendingMDecoded = false
    releaseSharedTicker()
  }

  function attachAmbient() {
    state.value = "idle"
    instance = subscriber
    ensureSharedTicker()
    ensurePending()
  }

  watch(poolReady, ready => {
    if (ready && state.value === "intro") {
      pickAndCommit()
      attachAmbient()
    }
  })

  function dismissIntro() {
    // Idle entry now happens automatically when poolReady fires.
    // This catches the edge case where pool was already ready before the watcher ran.
    if (state.value === "intro" && poolReady.value) {
      pickAndCommit()
      attachAmbient()
    }
  }

  function stopCharge() {
    if (chargeRaf !== null) {
      cancelAnimationFrame(chargeRaf)
      chargeRaf = null
    }
  }

  function stopSpin() {
    if (spinRaf !== null) {
      cancelAnimationFrame(spinRaf)
      spinRaf = null
    }
  }

  function chargeStep() {
    if (state.value !== "charging") return
    const charge = clamp(
      (performance.now() - chargeStart) / CHARGE_FULL_MS,
      0,
      1,
    )
    chargeLevel.value = charge

    // Press grows the held letter further, charging toward a near-takeover.
    const target =
      pressedLetter.value === "c"
        ? map(charge, 0, 1, HOVER_GROW, CHARGE_GROW)
        : map(charge, 0, 1, 1 - HOVER_GROW, 1 - CHARGE_GROW)
    setSpringTarget(target, { k: 600, c: 40 })

    // Tension tremble — amplitude ramps up sharply near full charge, so a held
    // letter feels like it's straining harder the closer it gets to bursting.
    const tremble = map(easeIn(charge), 0, 1, 0, CHARGE_TREMBLE_MAX)
    if (tremble > 0) kickSpring((Math.random() * 2 - 1) * tremble)

    chargeRaf = requestAnimationFrame(chargeStep)
  }

  function flashStep(spinProgress: number) {
    const pair = pickPair()
    if (!pair) return
    const [c, m] = pair
    lastC = c
    lastM = m
    cImage.value = c
    mImage.value = m

    // Shiver the rows — amplitude calms as the reel slows
    const shiverAmp =
      map(spinProgress, 0, 1, 0.04, 0) * (Math.random() < 0.5 ? 1 : -1)
    kickSpring(shiverAmp)
  }

  function settle() {
    state.value = "result"
    chargeLevel.value = 0

    // Both rows flip to the winner chosen at release, at the same instant.
    const winner = finalImage || cImage.value || mImage.value
    if (winner) {
      cImage.value = winner
      mImage.value = winner
    }
    finalImage = null

    // Bouncy spring back to equal heights
    setSpringTarget(0.5, { k: 300, c: 13 })
    const kickDir = pressedLetter.value === "c" ? -0.6 : 0.6
    kickSpring(kickDir)
  }

  function startSpin(finalCharge: number) {
    const spinDuration = map(finalCharge, 0, 1, SPIN_MS_MIN, SPIN_MS_MAX)
    const startInterval = map(
      finalCharge,
      0,
      1,
      START_INTERVAL_MAX,
      START_INTERVAL_MIN,
    )
    const spinStart = performance.now()
    let lastFlip = 0

    function spinStep(now: number) {
      if (state.value !== "spinning") return
      const progress = clamp((now - spinStart) / spinDuration, 0, 1)
      const interval = map(easeIn(progress), 0, 1, startInterval, END_INTERVAL)
      if (now - lastFlip >= interval) {
        lastFlip = now
        flashStep(progress)
      }
      if (progress >= 1) {
        settle()
        return
      }
      spinRaf = requestAnimationFrame(spinStep)
    }
    spinRaf = requestAnimationFrame(spinStep)
  }

  function onMouseMove(letter: "c" | "m", x: number, y: number) {
    if (state.value !== "idle") return
    const tracker = letter === "c" ? lastMouseC : lastMouseM
    const dx = x - tracker.x
    const dy = y - tracker.y
    if (Math.sqrt(dx * dx + dy * dy) < MOUSE_THRESHOLD) return
    tracker.x = x
    tracker.y = y
    const next = pickSingleFor(letter)
    if (!next) return
    if (letter === "c") {
      cImage.value = next
      lastC = next
    } else {
      mImage.value = next
      lastM = next
    }
    if (mouseBothLetters) {
      const other: "c" | "m" = letter === "c" ? "m" : "c"
      const nextOther = pickSingleFor(other)
      if (nextOther) {
        if (other === "c") {
          cImage.value = nextOther
          lastC = nextOther
        } else {
          mImage.value = nextOther
          lastM = nextOther
        }
      }
    }
  }

  // ── Public interaction ─────────────────────────────────────────────────────

  function hoverTarget(letter: "c" | "m") {
    return letter === "c" ? HOVER_GROW : 1 - HOVER_GROW
  }

  function onHoverStart(letter: "c" | "m") {
    if (state.value !== "idle") return
    hoveredLetter.value = letter
    setSpringTarget(hoverTarget(letter), { k: 200, c: 24 })
  }

  function onHoverEnd(letter: "c" | "m") {
    if (hoveredLetter.value === letter) hoveredLetter.value = null
    if (state.value !== "idle") return
    // Defer to the other letter if it's still hovered, else relax to 50/50.
    const target = hoveredLetter.value ? hoverTarget(hoveredLetter.value) : 0.5
    setSpringTarget(target, { k: 200, c: 24 })
  }

  function onPressStart(letter: "c" | "m") {
    if (state.value === "result") {
      reset()
      return
    }
    if (state.value !== "idle") return
    detachAmbient()
    hoveredLetter.value = null
    pressedLetter.value = letter
    chargeLevel.value = 0
    state.value = "charging"
    chargeStart = performance.now()
    stopCharge()
    chargeRaf = requestAnimationFrame(chargeStep)
  }

  function rejectPress() {
    chargeLevel.value = 0

    // Overdamped return (c > 2·√k ⇒ no overshoot) = a firm, rigid "no".
    setSpringTarget(0.5, { k: 420, c: 60 })

    // A small outward nudge: the letter bulges a touch further, then the stiff
    // spring pulls it straight back — the minimum grow that signals resistance.
    const kickDir = pressedLetter.value === "c" ? 1 : -1
    kickSpring(kickDir * 3)

    pressedLetter.value = null
    attachAmbient() // back to interactive idle; spring settles in the background
  }

  function onPressEnd() {
    if (state.value !== "charging") return
    stopCharge()

    // Too-short press = a "click", not a hold: reject with a locked-door bounce
    // instead of spinning, so the gesture teaches "hold it down".
    const heldMs = performance.now() - chargeStart
    if (heldMs < MIN_HOLD_MS) {
      rejectPress()
      return
    }

    const finalCharge = Math.max(MIN_CHARGE, chargeLevel.value)
    state.value = "spinning"

    // Lock in the landing project now, so both rows can settle onto it together.
    finalImage = pickWinner()
    loadImage(finalImage)

    // Release: snap the grown letter back to a 50/50 split with an underdamped
    // spring + velocity kick, so it overshoots through even before spinning.
    const releaseK = map(finalCharge, 0, 1, 250, 480)
    const releaseC = map(finalCharge, 0, 1, 22, 14)
    setSpringTarget(0.5, { k: releaseK, c: releaseC })

    const kickMag = map(finalCharge, 0, 1, 3, 9)
    // Kick in the collapse direction (toward/through even) — c shrinks, m grows.
    const kickDir = pressedLetter.value === "c" ? -1 : 1
    kickSpring(kickDir * kickMag)

    startSpin(finalCharge)
  }

  function reset() {
    stopCharge()
    stopSpin()
    pressedLetter.value = null
    chargeLevel.value = 0
    setSpringTarget(0.5, { k: 170, c: 26 })
    attachAmbient()
  }

  onUnmounted(() => {
    stopCharge()
    stopSpin()
    stopSpringLoop()
    detachAmbient()
    imageCache.clear()
  })

  function stop() {
    stopCharge()
    stopSpin()
    pressedLetter.value = null
    chargeLevel.value = 0
    detachAmbient()
  }

  function pause() {
    if (
      state.value === "charging" ||
      state.value === "spinning" ||
      state.value === "result"
    )
      return
    detachAmbient()
    state.value = "paused"
  }

  function resume() {
    if (state.value !== "paused") return
    attachAmbient()
  }

  return {
    state,
    poolReady,
    cImage,
    mImage,
    readyCount,
    chargeLevel,
    pressedLetter,
    hoveredLetter,
    split,
    dismissIntro,
    stop,
    pause,
    resume,
    onPressStart,
    onPressEnd,
    onHoverStart,
    onHoverEnd,
    onMouseMove,
    reset,
  }
}
